"use client";

import { RiCloseLine, RiSearchLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Spinner } from "@workspace/ui/components/spinner";
import { Input } from "@workspace/ui/components/input";
import { useDebounce } from "@workspace/ui/hooks/use-debounce";
import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export interface EnhancedSearchInputProps {
	value: string;
	onValueChange: (value: string) => void;
	onDebouncedValueChange?: (value: string) => void;
	placeholder?: string;
	className?: string;
	debounceMs?: number;
	showClearButton?: boolean;
	showLoadingIndicator?: boolean;
	isLoading?: boolean;
	minLength?: number;
	autoFocus?: boolean;
}

export function EnhancedSearchInput({
	value,
	onValueChange,
	onDebouncedValueChange,
	placeholder = "Search...",
	className,
	debounceMs = 300,
	showClearButton = true,
	showLoadingIndicator = true,
	isLoading = false,
	minLength = 2,
	autoFocus = false,
}: EnhancedSearchInputProps) {
	const [localValue, setLocalValue] = useState(value);
	const debouncedValue = useDebounce(localValue, debounceMs);

	// Sync local value with external value
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	// Handle debounced value changes
	useEffect(() => {
		if (onDebouncedValueChange && debouncedValue.length >= minLength) {
			onDebouncedValueChange(debouncedValue);
		} else if (onDebouncedValueChange && debouncedValue.length === 0) {
			// Also trigger on empty to clear results
			onDebouncedValueChange(debouncedValue);
		}
	}, [debouncedValue, onDebouncedValueChange, minLength]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value;
			setLocalValue(newValue);
			onValueChange(newValue);
		},
		[onValueChange],
	);

	const handleClear = useCallback(() => {
		setLocalValue("");
		onValueChange("");
		if (onDebouncedValueChange) {
			onDebouncedValueChange("");
		}
	}, [onValueChange, onDebouncedValueChange]);

	const showClear = showClearButton && localValue.length > 0;
	const showLoader = showLoadingIndicator && isLoading;

	return (
		<div className={cn("relative", className)}>
			<RiSearchLine className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
			<Input
				placeholder={placeholder}
				className={cn("h-10 pl-9", showClear || showLoader ? "pr-9" : "pr-3")}
				value={localValue}
				onChange={handleChange}
				autoFocus={autoFocus}
			/>
			{showLoader && (
				<div className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3">
					<Spinner className="h-4 w-4 animate-spin text-muted-foreground" />
				</div>
			)}
			{showClear && !showLoader && (
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="-translate-y-1/2 absolute top-1/2 right-1 h-7 w-7 p-0"
					onClick={handleClear}
				>
					<RiCloseLine className="h-4 w-4" />
					<span className="sr-only">Clear search</span>
				</Button>
			)}
		</div>
	);
}

// Simplified version that matches the original SearchInput API but with debouncing
interface SimpleSearchInputProps {
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onDebouncedChange?: (value: string) => void;
	placeholder?: string;
	className?: string;
	debounceMs?: number;
}

export function SearchInputWithDebounce({
	value,
	onChange,
	onDebouncedChange,
	placeholder = "Search...",
	className,
	debounceMs = 300,
}: SimpleSearchInputProps) {
	const debouncedValue = useDebounce(value, debounceMs);

	useEffect(() => {
		if (onDebouncedChange) {
			onDebouncedChange(debouncedValue);
		}
	}, [debouncedValue, onDebouncedChange]);

	return (
		<div className={cn("relative", className)}>
			<RiSearchLine className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
			<Input
				placeholder={placeholder}
				className="h-10 pl-9"
				value={value}
				onChange={onChange}
			/>
		</div>
	);
}
